import React, { useEffect, useRef, useState } from 'react';
import WikiIcon from '../../shared/WikiIcon';
import { BRAIN_NEW_COPY } from './brainCopy';
import {
    BRAIN_ANSWER_TYPES, BRAIN_EFFECTS, BRAIN_MAX_OPTIONS, brainNewForm, brainNewKind, brainNewPlacement,
    brainNewRequest, brainNewSaveLabel, brainNewTitle, brainWhenOptions, validateBrainNewForm,
} from './brainTreeModel';
import { brainErrorText, useBrainCreate } from './useBrainTree';

const C = BRAIN_NEW_COPY;

function FieldLabel({ htmlFor, children, required = false }) {
    return (
        <label className="wk-t-label wk-brain-field-label" htmlFor={htmlFor}>
            {children}
            {required && <span className="wk-brain-required"> *</span>}
        </label>
    );
}

function ChipPicker({ options, value, onPick, testId }) {
    return (
        <div className="wk-brain-chippick" role="radiogroup" data-testid={testId}>
            {options.map((label) => (
                <button
                    key={label}
                    type="button"
                    role="radio"
                    aria-checked={value === label}
                    className={`wk-brain-pickchip${value === label ? ' is-on' : ''}`}
                    onClick={() => onPick(label)}
                >
                    {label}
                </button>
            ))}
        </div>
    );
}

/**
 * New-node modal (prototype `brNewOn`, E1 §1.5): branch / group / check forms,
 * the parent-answer picker, typed answers (Yes / No · Pick one · Score 0–4),
 * the WHEN IT FIRES picker, client validation with the 8 verbatim messages
 * and server 422s shown verbatim. Creates through the API and hands the
 * response to `onCreated(kind, response)`.
 *
 * Props: `mode` ('branch'|'group'|'child'|'sibling'), `anchorId`, `model`
 * (buildBrainTreeModel), `onCancel`, `onCreated`. Mount it with a fresh `key`
 * per open — the form resets like the prototype's `brOpenNew`.
 */
export default function BrainNewNodeModal({ mode, anchorId, model, onCancel, onCreated }) {
    const [form, setForm] = useState(() => brainNewForm(mode, anchorId));
    const [error, setError] = useState(null);
    const firstFieldRef = useRef(null);
    const create = useBrainCreate();

    const { byId, branch } = model;
    const anchor = byId[anchorId] || Object.values(byId).find((n) => n.kind === 'root') || null;
    const kind = brainNewKind(mode, anchor);
    const whenOptions = brainWhenOptions(mode, kind, anchor);
    const title = brainNewTitle(mode, kind);
    const isNamed = kind === 'branch' || kind === 'group';

    useEffect(() => {
        if (firstFieldRef.current) firstFieldRef.current.focus();
    }, []);

    useEffect(() => {
        const onKey = (event) => {
            if (event.key === 'Escape') onCancel();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onCancel]);

    // Prototype `brSetNew`: every edit clears the error.
    const patch = (next) => {
        setForm((prev) => ({ ...prev, ...next }));
        setError(null);
    };
    const setOption = (index, key, value) => {
        const options = form.options.map((o, i) => (i === index ? { ...o, [key]: value } : o));
        patch({ options });
    };
    const setLevel = (index, value) => {
        const levels = form.levels.slice();
        levels[index] = value;
        patch({ levels });
    };

    const save = async () => {
        if (create.isPending) return;
        const message = validateBrainNewForm(form, kind);
        if (message) {
            setError(message);
            return;
        }
        const request = brainNewRequest(form, kind, { branchId: branch?.id });
        try {
            const response = await create.mutateAsync(request);
            onCreated(kind, response || {});
        } catch (err) {
            setError(brainErrorText(err));
        }
    };

    const placement = brainNewPlacement({ mode, kind, anchor, byId, branchName: branch?.name });

    return (
        <>
            <div className="wk-brain-modal-backdrop" onClick={onCancel} data-testid="brain-new-backdrop" />
            <div
                className="wk-brain-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="brain-new-title"
                data-testid="brain-new-modal"
                data-kind={kind}
            >
                <div className="wk-brain-modal-head">
                    <span className="wk-brain-modal-tile" aria-hidden="true"><WikiIcon name="brain" size={16} /></span>
                    <h2 id="brain-new-title" className="wk-t-h wk-brain-modal-title">{title}</h2>
                    <button type="button" className="wk-brain-iconbtn wk-brain-modal-close" title={C.close} aria-label={C.close} onClick={onCancel}>
                        <WikiIcon name="x" size={15} />
                    </button>
                </div>
                <div className="wk-brain-modal-place" data-testid="brain-new-placement">
                    <WikiIcon name="git-merge" size={13} />
                    <span className="wk-ellipsis">{placement}</span>
                </div>

                <div className="wk-brain-modal-body">
                    {isNamed && (
                        <>
                            <div className="wk-brain-field">
                                <FieldLabel htmlFor="brain-new-name" required>
                                    {kind === 'branch' ? C.branchName : C.groupName}
                                </FieldLabel>
                                <input
                                    id="brain-new-name"
                                    ref={firstFieldRef}
                                    className="wk-input"
                                    value={form.name}
                                    onChange={(e) => patch({ name: e.target.value })}
                                    placeholder={kind === 'branch' ? C.branchNamePh : C.groupNamePh}
                                    maxLength={120}
                                />
                            </div>
                            <div className="wk-brain-field">
                                <FieldLabel htmlFor="brain-new-desc">{C.description}</FieldLabel>
                                <textarea
                                    id="brain-new-desc"
                                    className="wk-textarea wk-brain-desc"
                                    value={form.description}
                                    onChange={(e) => patch({ description: e.target.value })}
                                    placeholder={kind === 'branch' ? C.branchDescPh : C.groupDescPh}
                                />
                            </div>
                        </>
                    )}

                    {kind === 'check' && (
                        <>
                            {whenOptions.length > 1 && (
                                <div className="wk-brain-field">
                                    <span className="wk-t-label wk-brain-field-label">{C.whenLabel}</span>
                                    <ChipPicker
                                        options={whenOptions}
                                        value={form.when || 'Always'}
                                        onPick={(when) => patch({ when })}
                                        testId="brain-new-when"
                                    />
                                    <p className="wk-t-meta">{C.whenHelper}</p>
                                </div>
                            )}
                            <div className="wk-brain-field">
                                <FieldLabel htmlFor="brain-new-question" required>{C.questionLabel}</FieldLabel>
                                <input
                                    id="brain-new-question"
                                    ref={firstFieldRef}
                                    className="wk-input"
                                    value={form.question}
                                    onChange={(e) => patch({ question: e.target.value })}
                                    placeholder={C.questionPh}
                                />
                                <p className="wk-t-meta">{C.questionHelper}</p>
                            </div>
                            <div className="wk-brain-field">
                                <span className="wk-t-label wk-brain-field-label">{C.typeLabel}</span>
                                <div className="wk-brain-seg" role="radiogroup" aria-label="Answer type" data-testid="brain-new-type">
                                    {BRAIN_ANSWER_TYPES.map((t) => (
                                        <button
                                            key={t.id}
                                            type="button"
                                            role="radio"
                                            aria-checked={form.type === t.id}
                                            className={`wk-brain-seg-item${form.type === t.id ? ' is-on' : ''}`}
                                            onClick={() => patch({ type: t.id })}
                                        >
                                            {t.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {form.type === 'yes_no' && (
                                <div className="wk-brain-yesno">
                                    <div className="wk-brain-field">
                                        <FieldLabel htmlFor="brain-new-yes" required>{C.yesLabel}</FieldLabel>
                                        <input
                                            id="brain-new-yes"
                                            className="wk-input"
                                            value={form.yes}
                                            onChange={(e) => patch({ yes: e.target.value })}
                                            placeholder={C.yesPh}
                                        />
                                    </div>
                                    <div className="wk-brain-field">
                                        <FieldLabel htmlFor="brain-new-no">{C.noLabel}</FieldLabel>
                                        <input
                                            id="brain-new-no"
                                            className="wk-input"
                                            value={form.no}
                                            onChange={(e) => patch({ no: e.target.value })}
                                            placeholder={C.noPh}
                                        />
                                    </div>
                                </div>
                            )}

                            {form.type === 'pick_one' && (
                                <div className="wk-brain-field" data-testid="brain-new-options">
                                    <span className="wk-t-label wk-brain-field-label">{C.optionsLabel}</span>
                                    <div className="wk-brain-optgrid wk-brain-optgrid--head" aria-hidden="true">
                                        <span />
                                        <span>{C.optionHead[0]}</span>
                                        <span>{C.optionHead[1]}</span>
                                        <span>{C.optionHead[2]}</span>
                                        <span />
                                    </div>
                                    {form.options.map((option, index) => (
                                        <div key={index} className="wk-brain-optgrid" data-testid="brain-new-option-row">
                                            <span className="wk-brain-optnum">{index + 1}</span>
                                            <input
                                                className="wk-input wk-brain-input-sm"
                                                aria-label={`Option ${index + 1} label`}
                                                value={option.label}
                                                onChange={(e) => setOption(index, 'label', e.target.value)}
                                                placeholder={C.optionPh[0]}
                                            />
                                            <input
                                                className="wk-input wk-brain-input-sm"
                                                aria-label={`Option ${index + 1} what it means`}
                                                value={option.what}
                                                onChange={(e) => setOption(index, 'what', e.target.value)}
                                                placeholder={C.optionPh[1]}
                                            />
                                            <input
                                                className="wk-input wk-brain-input-sm"
                                                aria-label={`Option ${index + 1} not for`}
                                                value={option.not}
                                                onChange={(e) => setOption(index, 'not', e.target.value)}
                                                placeholder={C.optionPh[2]}
                                            />
                                            {form.options.length > 2 ? (
                                                <button
                                                    type="button"
                                                    className="wk-brain-iconbtn wk-brain-optremove"
                                                    title={C.remove}
                                                    aria-label={`${C.remove} option ${index + 1}`}
                                                    onClick={() => patch({ options: form.options.filter((_, j) => j !== index) })}
                                                >
                                                    <WikiIcon name="x" size={13} />
                                                </button>
                                            ) : <span />}
                                        </div>
                                    ))}
                                    <div className="wk-brain-optgrid wk-brain-optgrid--other">
                                        <span className="wk-brain-optnum"><WikiIcon name="lock" size={12} /></span>
                                        <div className="wk-brain-optother">{C.otherRow}</div>
                                        <div className="wk-t-meta">{C.otherHelper}</div>
                                        <span />
                                    </div>
                                    <button
                                        type="button"
                                        className="wk-brain-addopt"
                                        data-testid="brain-new-add-option"
                                        onClick={() => {
                                            if (form.options.length < BRAIN_MAX_OPTIONS) {
                                                patch({ options: [...form.options, { label: '', what: '', not: '' }] });
                                            }
                                        }}
                                    >
                                        <WikiIcon name="plus" size={13} />
                                        {C.addOption}
                                    </button>
                                </div>
                            )}

                            {form.type === 'score' && (
                                <div className="wk-brain-field" data-testid="brain-new-levels">
                                    <span className="wk-t-label wk-brain-field-label">{C.levelsLabel}</span>
                                    {form.levels.map((level, index) => (
                                        <div key={index} className="wk-brain-level">
                                            <span className="wk-brain-level-n">{index}</span>
                                            <input
                                                className="wk-input wk-brain-input-sm"
                                                aria-label={`Level ${index}`}
                                                value={level}
                                                onChange={(e) => setLevel(index, e.target.value)}
                                                placeholder={C.levelHints[index] || ''}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="wk-brain-field">
                                <span className="wk-t-label wk-brain-field-label">{C.effectLabel}</span>
                                <ChipPicker
                                    options={BRAIN_EFFECTS}
                                    value={form.effect}
                                    onPick={(effect) => patch({ effect })}
                                    testId="brain-new-effect"
                                />
                            </div>
                        </>
                    )}

                    {error && (
                        <div className="wk-brain-modal-error" role="alert" data-testid="brain-new-error">
                            <WikiIcon name="alert-triangle" size={14} />
                            <span>{error}</span>
                        </div>
                    )}
                </div>

                <div className="wk-brain-modal-foot">
                    <span className="wk-t-meta wk-brain-modal-note">{C.footer}</span>
                    <button type="button" className="wk-btn wk-btn--ghost wk-brain-modal-btn" onClick={onCancel}>
                        {C.cancel}
                    </button>
                    <button
                        type="button"
                        className="wk-btn wk-btn--primary wk-brain-modal-btn"
                        onClick={save}
                        disabled={create.isPending}
                        data-testid="brain-new-save"
                    >
                        <WikiIcon name="check" size={14} />
                        {brainNewSaveLabel(kind)}
                    </button>
                </div>
            </div>
        </>
    );
}
